import React, { useState, useEffect } from 'react';
import { useTranslate } from 'react-admin';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import moment from 'moment';

import Background from './Background';
import DialogBox from './DialogBox';
import Enemy from './Enemy';
import Ally from './Ally';
import HealthBar from './HealthBar';
import useSkills from './useSkills';
import animationsSx from './animationsSx';

import { fetchTimeEntriesStats } from '../../../../../services/api';


const Team = ({ stats = [], month, projectId }) => {
    const [projectStats, setProjectStats] = useState();
    const [loading, setLoading] = useState(false);
    const [steps, setSteps] = useState([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [text, setText] = useState('');
    const [hp, setHp] = useState({ project: 100, users: {} });
    const [flashAnimation, setFlashAnimation] = useState(false);
    const [attackAnimation, setAttackAnimation] = useState(false);
    const translate = useTranslate();
    const skills = useSkills();
    const project = stats?.data?.find(item => item?.group_by_id === projectId);

    const handleEmptyText = () => {
        setText(
            <Box sx={{ fontSize: '25px' }}>
                <Box sx={{ display: 'inline', fontStyle: 'italic' }}>
                    "Durant un moment, il ne se produisit rien. Puis, au bout d'une seconde à peu près, il continua de ne rien se produire."
                </Box>
                <Box sx={{ display: 'inline', fontSize: '20px', color: '#C3C3C3' }}>
                    (H2G2)
                </Box>
            </Box>
        );
    };

    useEffect(() => {
        const fetchStats = async () => {
            if (!stats || !projectId) {
                setProjectStats(null);
                handleEmptyText();
                return;
            }

            setLoading(true);

            const momentMonth = month ? moment(`${month}-01`) : moment();
            const startDate = momentMonth.startOf('month').format('YYYY-MM-DD');
            const endDate = momentMonth.endOf('month').format('YYYY-MM-DD');
            const filters = {
                start_date: startDate,
                end_date: endDate,
                freq: 'monthly',
                group_by: 'user',
                project_id: projectId
            };
            let newProjectStats = await fetchTimeEntriesStats(filters);

            let newData;
            if (newProjectStats?.data?.length > 0) {
                const promises = newProjectStats.data.map(async item => {
                    const response = await fetchTimeEntriesStats({ ...filters, project_id: projectId, user_id: item.group_by_id, group_by: 'type' });
                    const types = response?.data?.sort((a, b) => b.value - a.value);
                    return { ...item, types };
                });
                newData = await Promise.all(promises);
            }
            newData = newData?.sort((a, b) => b.value - a.value);
            setProjectStats({ ...newProjectStats, data: newData });

            let usersHp = {};
            const { data } = stats;
            let defaultSteps = [];
            let lastStepText = '';
            if (data?.length > 0) {
                defaultSteps = [
                    { text: `${newData?.map(item => item?.name?.toUpperCase())?.join(', ')} ${newData?.length > 1 ? 'veulent' : 'veut'} se battre` }
                ];
            }

            const newSteps = newData?.reduce((acc, item) => {
                const getEfficacity = (value) => {
                    if (value > 50) {
                        return translate('C\'est super efficace!');
                    }
                    else if (value > 25) {

                    }
                    else {
                        return translate('Ce n\'est pas très efficace...');
                    }
                };

                let items = [
                    {
                        text: `${project?.name?.toUpperCase()} lance ${skills.getSkill(item?.types[0]?.name, 'enemy')?.toUpperCase()}`,
                        damage: 'user',
                        value: item?.value,
                        user_id: item?.group_by_id
                    }
                ];
                let tmpText = getEfficacity((item?.value / newProjectStats?.total) * 100);
                if (tmpText) {
                    items.push({ text: tmpText });
                }
                items.push({
                    text: `${item?.name.toUpperCase()} lance ${skills.getSkill(item?.types[0]?.name, 'team')?.toUpperCase()}`,
                    damage: 'project',
                    value: item?.value,
                    user_id: item?.group_by_id
                });
                if (tmpText) {
                    items.push({ text: tmpText });
                }

                usersHp[item?.group_by_id] = 100;

                lastStepText = lastStepText + `\n${item?.name?.toUpperCase()}: ${item?.value}h (${item?.types?.map(type => type?.name)?.join(', ')})`;

                return [...acc, ...items];
            }, defaultSteps);

            setSteps(newSteps || []);
            setCurrentStep(0);
            if (newSteps?.length > 0) {
                setText(newSteps[0]?.text);
            }
            else {
                handleEmptyText();
            }
            setHp({ project: 100, users: usersHp });

            if (lastStepText) {
                newSteps?.push({
                    text: <Box sx={{ fontSize: '30px', lineHeight: '35px' }}>{`Résumé${lastStepText}\n\nTotal: ${newProjectStats?.total}h`}</Box>
                });
            }

            setLoading(false);
        }

        fetchStats();
    }, [stats, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (currentStep < steps?.length && steps[currentStep]) {
            const step = steps[currentStep];
            setText(step.text);

            setTimeout(() => {
                if (step?.damage === 'project') {
                    setAttackAnimation({ type: 'user', id: step?.user_id })
                }
                else if (step?.damage === 'user') {
                    setAttackAnimation({ type: 'project', id: step?.user_id });
                }

                setTimeout(() => {
                    setAttackAnimation(false);
                    if (step?.damage === 'project') {
                        setFlashAnimation({ type: 'project', id: step?.user_id });
                        const damage = hp?.project - ((step?.value / projectStats?.total) * 100);
                        setHp(state => ({ ...state, 'project': damage }));
                    }
                    else if (step?.damage === 'user') {
                        setFlashAnimation({ type: 'user', id: step?.user_id });
                        const damage = hp?.users[step?.user_id] - ((step?.value / projectStats?.total) * 100)
                        setHp(state => ({ ...state, 'users': { ...state?.users, [step?.user_id]: damage } }));
                    }

                    setTimeout(() => {
                        setFlashAnimation(false);
                    }, 3000);
                }, 1000);
            }, 500);
        }
    }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleNext = () => {
        setCurrentStep(state => state + 1);
    };

    const handleSkip = () => {
        setCurrentStep(steps?.length - 1);

        const usersHp = projectStats?.data?.reduce((acc, item) => {
            return { ...acc, [item?.group_by_id]: 100 - (item?.value / projectStats?.total) * 100 };
        }, {});

        setHp({
            project: 0,
            users: usersHp
        });
    };

    const max = stats?.data?.length > 0 ? stats?.data[0]?.value / stats?.total : 0;
    const min = stats?.data?.length > 0 ? stats?.data[projectStats?.data?.length - 1]?.value / projectStats?.total : 0;

    return (
        <>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
                    <CircularProgress size={60} thickness={8} />
                </Box>
            ) : (
                <Box position="relative">
                    <Box sx={{ position: 'absolute', maxWidth: '85%', bottom: '80px', left: '0', zIndex: 100 }}>
                        {projectStats?.data?.map((item, index) => {
                            const activeFlashAnimation = flashAnimation?.type === 'user' && flashAnimation?.id === item?.group_by_id;
                            const activeAttackAnimation = attackAnimation?.type === 'user' && attackAnimation?.id === item?.group_by_id;
                            const minScale = 0.5;
                            const scale = minScale + (1 - minScale) * Math.max(minScale, Math.min(minScale + (1 - minScale) * ((item?.value / stats?.total) - min) / (max - min), 1));
                            return (
                                <Box key={index} sx={{ display: 'inline-flex', position: 'relative' }}>
                                    <HealthBar
                                        name={item?.name}
                                        value={hp?.users[item?.group_by_id]}
                                        sx={{ top: '-200px', left: '50%' }}
                                        tooltipText={`${item?.value}h/${projectStats?.total}h (${((item?.value / projectStats?.total) * 100).toFixed(1)}%)`}
                                    />
                                    <Box sx={{ transform: `scale(${scale})` }}>
                                        <Box sx={activeFlashAnimation ? animationsSx.flash : activeAttackAnimation ? animationsSx.attack : {}}>
                                            <Ally firstname={item?.name} width="250px" height="250px" />
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                    {project && (
                        <Box sx={{ position: 'absolute', maxWidth: '65%', bottom: '160px', right: '50px', zIndex: 99 }}>
                            <Box sx={{ display: 'inline-flex', position: 'relative' }}>
                                <HealthBar
                                    name={project?.name}
                                    value={hp?.project}
                                    tooltipText={
                                        <>
                                            {`${project?.value}h/${stats?.total}h (${translate('hours worked/total hours')})`}<br />
                                            {`${translate('Most commonly used types')}: ${project?.types?.map(type => type?.name)?.join(', ')}`}
                                        </>
                                    }
                                />
                                <Box sx={flashAnimation?.type === 'project' ? animationsSx.flash : attackAnimation?.type === 'project' ? animationsSx.attack : {}}>
                                    <Enemy color={project?.color} name={project?.name} />
                                </Box>
                            </Box>
                        </Box>
                    )}
                    <Background />
                    <DialogBox onNext={handleNext} onSkip={handleSkip} nextDisabled={currentStep >= steps?.length - 1} skipDisabled={currentStep >= steps?.length - 1}>
                        {text}
                    </DialogBox>
                </Box >
            )}
        </>
    );
};

export default Team;